// API client: auth tokens, automatic refresh, idempotent retries, Arabic error messages.
// Relative to the folder the panel is served from, so it works on a domain root ("/api/v1")
// and under a sub-path such as https://example.com/tasks/ ("/tasks/api/v1").
const BASE = window.SMART_TEAM_API || location.pathname.replace(/[^/]*$/, "") + "api/v1";

let access = null;
let accessExp = 0;
const store = () => (localStorage.getItem("st_remember") === "1" ? localStorage : sessionStorage);
const listeners = new Set();

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const auth = {
  onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  set(session, remember) {
    if (remember !== undefined) localStorage.setItem("st_remember", remember ? "1" : "0");
    access = session.access_token;
    accessExp = Date.parse(session.access_expires_at);
    store().setItem("st_refresh", session.refresh_token);
  },
  hasSession() { return !!(access || store().getItem("st_refresh")); },
  clear() {
    access = null;
    localStorage.removeItem("st_refresh");
    sessionStorage.removeItem("st_refresh");
    // local cached data belongs to the signed-out account
    Object.keys(localStorage).filter((k) => k.startsWith("st_draft") || k.startsWith("st_upload"))
      .forEach((k) => localStorage.removeItem(k));
    listeners.forEach((fn) => fn(null));
  },
};

export function newKey() {
  return (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
}

let refreshing = null;
async function refresh() {
  const token = store().getItem("st_refresh");
  if (!token) throw new ApiError(401, "unauthorized", "يجب تسجيل الدخول");
  if (!refreshing) {
    refreshing = fetch(BASE + "/auth/refresh", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: token }),
    }).then(async (r) => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { auth.clear(); throw new ApiError(r.status, body.error?.code, body.error?.message || "انتهت الجلسة"); }
      auth.set(body);
    }).finally(() => { refreshing = null; });
  }
  return refreshing;
}

async function token() {
  if (!access || Date.now() > accessExp - 30_000) await refresh();
  return access;
}

/**
 * request("POST", "/tasks", body, {key}) — mutations carry an Idempotency-Key so a retry after a
 * dropped connection never performs the action twice. Pass the same `key` when retrying.
 */
export async function request(method, path, body, opts = {}) {
  const headers = { Accept: "application/json" };
  if (opts.auth !== false) headers.Authorization = "Bearer " + (await token());
  if (body !== undefined && !(body instanceof Blob)) headers["Content-Type"] = "application/json";
  if (method !== "GET" && opts.idempotent !== false) headers["Idempotency-Key"] = opts.key || newKey();
  Object.assign(headers, opts.headers || {});
  let res;
  try {
    res = await fetch(BASE + path, {
      method, headers, signal: opts.signal,
      body: body === undefined ? undefined : body instanceof Blob ? body : JSON.stringify(body),
    });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw new ApiError(0, "network", "تعذر الاتصال بالخادم. تحقق من الشبكة وحاول مجددًا");
  }
  if (res.status === 401 && opts.auth !== false && !opts._retried) {
    const b = await res.clone().json().catch(() => ({}));
    if (b.error?.code === "token_invalid") { access = null; return request(method, path, body, { ...opts, _retried: true }); }
    auth.clear();
  }
  if (opts.raw) return res;
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, data?.error?.code || "error",
      data?.error?.message || "حدث خطأ غير متوقع", data?.error?.details);
  }
  return data;
}

export const api = {
  get: (p, o) => request("GET", p, undefined, o),
  post: (p, b = {}, o) => request("POST", p, b, o),
  patch: (p, b, o) => request("PATCH", p, b, o),
  put: (p, b, o) => request("PUT", p, b, o),
  del: (p, o) => request("DELETE", p, undefined, o),
  async login(email, password, remember) {
    const s = await request("POST", "/auth/login", { email, password }, { auth: false, idempotent: false });
    auth.set(s, remember);
    return s.user;
  },
  async acceptInvite(tokenValue, password) {
    const s = await request("POST", "/auth/accept-invite", { token: tokenValue, password }, { auth: false, idempotent: false });
    auth.set(s, false);
    return s.user;
  },
  async logout() {
    try { await request("POST", "/auth/logout", {}, { idempotent: false }); } catch { /* signing out anyway */ }
    auth.clear();
  },
  /** Download a protected file through the API (permission checked server-side). */
  async download(path, filename) {
    const res = await request("GET", path, undefined, { raw: true });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new ApiError(res.status, b.error?.code, b.error?.message || "تعذر التنزيل");
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  async blobUrl(path) {
    const res = await request("GET", path, undefined, { raw: true });
    if (!res.ok) throw new ApiError(res.status, "error", "تعذر تحميل المعاينة");
    return URL.createObjectURL(await res.blob());
  },
};

export const qs = (o) => {
  const p = Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== "");
  return p.length ? "?" + new URLSearchParams(p).toString() : "";
};
